import { CollectionElement } from "js/elements/base/CollectionElement";
import { _ } from "js/vendor";
import { ConnectorType, NodeType } from "common/constants";
import {
    BulletTextNodeElement,
    ContentAndTextNodeElement, ContentNodeElement,
    FlexCircleNodeElement,
    NumberedTextNodeElement, TextInShapeNodeElement, TextNodeElement
} from "../base/NodeElement";
import ConnectorGroup from "./connectors/ConnectorGroup";
import log from "js/core/logger";

class IconCircle extends CollectionElement {
    get connectorDefaultModel() {
        return {
            startDecoration: "none",
            // Migrating from the old model
            endDecoration: this.model.show_arrows !== false ? "arrow" : "none",
            lineStyle: "solid",
            lineWeight: 3,
            canChangeConnectorType: false
        };
    }

    get connectorType() {
        return this.model.shape_is_polygon === true ? ConnectorType.STRAIGHT : ConnectorType.ARC;
    }

    getChildItemType(model) {
        switch (model.nodeType) {
            case NodeType.FLEX_CIRCLE:
                return FlexCircleNodeElement;
            case NodeType.BULLET_TEXT:
                return BulletTextNodeElement;
            case NodeType.TEXT:
                return TextNodeElement;
            case NodeType.CONTENT_AND_TEXT:
                return ContentAndTextNodeElement;
            case NodeType.NUMBERED_TEXT:
                return NumberedTextNodeElement;
            case NodeType.CONTENT:
                return ContentNodeElement;
            case NodeType.BOX:
            case NodeType.CAPSULE:
            case NodeType.DIAMOND:
            case NodeType.CIRCLE:
                return TextInShapeNodeElement;
            default:
                return ContentAndTextNodeElement;
        }
    }

    getChildOptions(model) {
        return { canChangeTextDirection: false };
    }

    getAllowedNodeTypes(node) {
        return [
            NodeType.CONTENT_AND_TEXT,
            NodeType.BOX,
            NodeType.CIRCLE,
            NodeType.CAPSULE,
            NodeType.TEXT,
            NodeType.BULLET_TEXT,
            NodeType.NUMBERED_TEXT,
            NodeType.CONTENT,
        ];
    }

    getNodeSelectionUIType(node) {
        return "IconCircleItemSelection";
    }

    get defaultItemData() {
        return {
            nodeType: _.last(this.itemElements).nodeType
        };
    }

    addItem(props, index) {
        const item = super.addItem(props, index);

        // Index can be undefined, in this case the item is pushed to the end of the items array
        let actualIndex = typeof index === "number" ? index : this.itemElements.length - 1;

        const indexBefore = actualIndex === 0 ? this.itemElements.length - 1 : (actualIndex - 1) % this.itemElements.length;
        const indexAfter = (actualIndex + 1) % this.itemElements.length;

        const itemIdBefore = this.itemElements[indexBefore].id;
        const itemIdAfter = this.itemElements[indexAfter].id;

        // Removing the connector between the previous element and the next element
        const oldConnector = this.model.connectors.items.find(connector => connector.source === itemIdBefore || connector.target === itemIdAfter);
        this.model.connectors.items.remove(oldConnector);

        // Adding a connector from the previous element to the new element
        this.model.connectors.items.push({
            ...oldConnector,
            id: null,
            source: itemIdBefore,
            target: item.id
        });

        // Adding a connector from the new element to the next element
        this.model.connectors.items.push({
            ...oldConnector,
            id: null,
            source: item.id,
            target: itemIdAfter
        });

        return item;
    }

    deleteItem(itemId) {
        // We'll use this connector to copy it's properties to the new connectors
        const oldConnector = this.model.connectors.items.find(connector => connector.target === itemId);

        // Removing the connectors from and to the deleted item
        _.remove(this.model.connectors.items, c => c.source === itemId || c.target === itemId);

        // Building a list of elements w/o the item
        const filteredElements = this.itemElements
            .filter(x => x.id !== itemId);
        if (filteredElements.length > 1) {
            // Building a list of connectors we need
            const connectorsNeeded = filteredElements
                .map((element, idx) => ({
                    source: element.id,
                    target: filteredElements[(idx + 1) % filteredElements.length].id
                }));
            // Finding ones that missing and recreating them
            connectorsNeeded
                .filter(({ source, target }) => !this.model.connectors.items.some(connector =>
                    connector.source === source && connector.target === target
                ))
                .forEach(({ source, target }) => {
                    this.model.connectors.items.push({
                        ...oldConnector,
                        id: null,
                        source,
                        target
                    });
                });
        }

        super.deleteItem(itemId);
    }

    _migrate_8() {
        super._migrate_8();

        this.model.connectors = {
            items: []
        };
    }

    _build() {
        // Migration from HubAndSpoke variation
        const hubItem = this.itemCollection.find(item => item.id === "hub");
        if (hubItem) {
            delete hubItem.userSize;

            let otherItemsType;
            for (const item of this.itemCollection.filter(item => item.id !== "hub")) {
                if (!otherItemsType) {
                    otherItemsType = item.nodeType;
                } else if (otherItemsType !== item.nodeType) {
                    break;
                }
            }
            if (otherItemsType) {
                hubItem.nodeType = otherItemsType;
            }
        }

        super._build();

        if (!this.model.connectors) {
            this.model.connectors = {
                items: []
            };
        } else if (this.model.connectors.items &&
            this.model.connectors.items.some(connector =>
                !this.itemElements.some(item => item.id === connector.source) ||
                !this.itemElements.some(item => item.id === connector.target)
            )
        ) {
            // Rebuild connectors if there are connectors pointing to non existing elements
            // because of a wrong migration
            log.warn("Malformed connectors model, rebuilding...");
            this.model.connectors = {
                items: []
            };
        }

        if (!this.model.connectors.items || this.model.connectors.items.length === 0) {
            this.itemElements.forEach((element, idx) => {
                const source = element.id;
                const target = this.itemElements[(idx + 1) % this.itemElements.length].id;

                if (source !== target) {
                    this.model.connectors.items.push({
                        ...this.connectorDefaultModel,
                        connectorType: this.connectorType,
                        source,
                        target
                    });
                }
            });
        } else {
            // Assigning the default model props and connector type to the existing connectors
            for (let i = 0; i < this.model.connectors.items.length; i++) {
                this.model.connectors.items[i] = {
                    ...this.connectorDefaultModel,
                    ...this.model.connectors.items[i],
                    connectorType: this.connectorType
                };
            }
        }

        this.connectors = this.addElement("connectors", () => ConnectorGroup, {
            model: this.model.connectors,
            containerElement: this,
            startPointsAreLocked: true,
            endPointsAreLocked: true,
            canDeleteConnectors: false
        });
        this.connectors.layer = -1;
    }

    _calcProps(props, options) {
        let { size, children } = props;

        const layouter = this.getLayouter(this.itemElements, size)
            .calcCircleLayout({
                clockwise: this.model.clockwise,
                shape_is_polygon: this.model.shape_is_polygon,
                ellipse_size: this.model.ellipse_size,
                topTextAbove: true,
                bottomTextBelow: true
            });

        children.setProps(this.connectors, size, { shape: layouter.shape });

        props.isFit = layouter.isFit;

        return { size };
    }

    getAnimations() {
        const animations = [];
        this.itemElements.forEach(node => {
            animations.push(...node.getAnimations());

            this.connectors.itemElements
                .filter(({ model }) => model.source === node.id)
                .forEach(connector => {
                    animations.push(...connector.getAnimations());
                });
        });
        return animations;
    }

    _migrate_6() {
        // because the layouter has changed, there were cases where an tray or left header could overlap the ellipse after migration
        // it was too much effort to make the layouter match exactly so this is a brute force just set ellipse_size to 1 if there was overlapping opportunity
        if (this.canvas.model.layout.headerPosition == "left" || (this.canvas.model.layout.trayLayout && this.canvas.model.layout.trayLayout != "none")) {
            this.model.ellipse_size = 1;
        }
    }
}

export const elements = {
    IconCircle
};
